import { useState, useEffect, useMemo } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TableSkeleton, exportCsv } from '@/components/dashboard/TableKit';
import { Pill } from '@/components/dashboard/ScreeningAddresses';
import api from '@/services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TenantPanel } from '@/components/dashboard/TenantPanel';
import { Sparkline } from '@/components/analytics/Sparkline';
import { DrillSheet } from '@/components/analytics/DrillSheet';
import { AiSummary } from '@/components/analytics/AiSummary';
import { CollectionStatus } from '@/components/analytics/CollectionStatus';
import { CollectionCurve } from '@/components/analytics/CollectionCurve';
import { AgingCard } from '@/components/analytics/AgingCard';
import { PaymentHabits } from '@/components/analytics/PaymentHabits';
import { RiskWatchlist } from '@/components/analytics/RiskWatchlist';
import { STATUS_LABEL } from '@/components/analytics/kit';

export default function AnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('90d');
  const [propSort, setPropSort] = useState('outstanding');
  const [filters, setFilters] = useState({ property_id: '', city: '', tenancy_status: '', payment_status: '' });
  const [data, setData] = useState(null);
  const [billingPlan, setBillingPlan] = useState(null);
  const [insights, setInsights] = useState(null);
  const [drill, setDrill] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const scope = useMemo(() => Object.fromEntries(Object.entries({ property_id: filters.property_id, city: filters.city }).filter(([, v]) => v)), [filters.property_id, filters.city]);
  const allowed = !!billingPlan;

  useEffect(() => { checkBillingPlan(); }, []);
  useEffect(() => {
    if (!allowed) return;
    let live = true;
    setInsights(null);
    api.get('/landlord/analytics/insights', { params: scope }).then(r => live && setInsights(r.data)).catch(() => live && setInsights(false));
    return () => { live = false; };
  }, [allowed, scope]);

  useEffect(() => {
    if (billingPlan) {
      fetchAnalytics();
    }
  }, [timeRange, filters, billingPlan]);

  const checkBillingPlan = async () => {
    try {
      const res = await api.get('/billing/current-plan');
      setBillingPlan(res.data);
    } catch {
      /* ignore */
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ time_range: timeRange, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
      const res = await api.get(`/landlord/analytics?${qs}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return <div data-testid="analytics-loading"><TableSkeleton rows={5} spans={[5, 3, 2, 2]} /></div>;
  }

  const k = data?.kpis || {};
  const months = data?.payments_by_month || [];
  const byProperty = data?.by_property || [];
  const attention = data?.attention || [];
  const mix = data?.payment_mix || {};
  const hasPrev = data?.period?.has_previous;
  const opts = data?.filters || { cities: [], properties: [] };
  const activeFilters = Object.values(filters).filter(Boolean).length;
  const setF = (key) => (v) => setFilters((f) => ({ ...f, [key]: v === 'all' ? '' : v, ...(key === 'city' && v !== 'all' ? { property_id: '' } : {}) }));
  const hasData = k.properties > 0 || k.reports > 0 || k.active_tenancies > 0;

  const RANGE_LABEL = { '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', '12m': 'Last 12 months', all: 'All time' };
  const judged = (mix.on_time || 0) + (mix.late || 0) + (mix.partial || 0) + (mix.missed || 0);

  const sortedProps = [...byProperty].sort((a, b) => {
    if (propSort === 'address') return a.address.localeCompare(b.address);
    if (propSort === 'collection_rate') return (a.collection_rate ?? 101) - (b.collection_rate ?? 101);
    return (b[propSort] || 0) - (a[propSort] || 0);
  });

  const tr = insights?.trends;
  const reportRows = (rows) => rows.map(r => ({ key: r.report_id, tenant_id: r.tenant_id, primary: r.tenant, secondary: `${r.where} · ${fmtDate(r.period_end)}${r.days_late ? ` · ${r.days_late} days late` : ''}`, value: r.owing ? money(r.owing) : STATUS_LABEL[r.status] || r.status, amount: r.owing, tone: r.status === 'on_time' ? 'text-teal-700' : r.status === 'missed' ? 'text-red-700' : 'text-amber-700' }));
  const drillReports = (title, params) => setDrill({ crumb: 'Analytics', title, load: () => api.get('/landlord/analytics/drill', { params: { kind: 'reports', ...scope, ...params } }).then(r => reportRows(r.data.rows)) });
  const drillTenancies = () => setDrill({ crumb: 'Analytics', title: 'Monthly rent roll', load: () => api.get('/landlord/analytics/drill', { params: { kind: 'tenancies', ...scope } }).then(r => r.data.rows.map(t => ({ key: t.tenancy_id, tenant_id: t.tenant_id, primary: t.tenant, secondary: t.where, value: `${money(t.rent)}/mo`, amount: t.rent }))) });
  const drillMonth = (m) => drillReports(`Payments for ${m.label}`, { month: m.key });
  const openTenant = (id) => { setDrill(null); setTenantId(id); };

  const exportProperties = () => exportCsv(`tenantra-analytics-properties-${timeRange}.csv`, sortedProps, [
    { label: 'Property', value: (r) => r.address }, { label: 'Active tenancies', value: (r) => r.active_tenancies },
    { label: 'Monthly rent', value: (r) => r.monthly_rent }, { label: 'Reports', value: (r) => r.reports },
    { label: 'On-time %', value: (r) => r.collection_rate ?? '' }, { label: 'Late/partial/missed', value: (r) => r.late },
    { label: 'Outstanding', value: (r) => r.outstanding }, { label: 'Open disputes', value: (r) => r.open_disputes },
  ]);

  return (
    <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`} data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-[15px] font-medium text-slate-800" data-testid="analytics-title">Portfolio analytics</h1>
          <p className="text-xs text-slate-500 mt-0.5">{RANGE_LABEL[timeRange]} · {fmtDate(data?.period?.start)} to {fmtDate(data?.period?.end)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[150px] h-8 text-xs bg-white border-slate-300" data-testid="time-range-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {Object.entries(RANGE_LABEL).map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={exportProperties} className="h-8 text-xs" disabled={!hasData} data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg px-4 py-2.5 flex flex-wrap items-center gap-2" data-testid="analytics-filters">
        <Select value={filters.city || 'all'} onValueChange={setF('city')}>
          <SelectTrigger className="h-8 w-[150px] text-xs bg-white border-slate-300" data-testid="filter-city"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" className="text-xs">All cities</SelectItem>
            {opts.cities.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.property_id || 'all'} onValueChange={setF('property_id')}>
          <SelectTrigger className="h-8 w-[240px] text-xs bg-white border-slate-300" data-testid="filter-property"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            <SelectItem value="all" className="text-xs">All properties</SelectItem>
            {opts.properties.filter((p) => !filters.city || p.address.toLowerCase().endsWith(filters.city.toLowerCase())).map((p) => <SelectItem key={p.id} value={p.id} className="text-xs">{p.address}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.tenancy_status || 'all'} onValueChange={setF('tenancy_status')}>
          <SelectTrigger className="h-8 w-[160px] text-xs bg-white border-slate-300" data-testid="filter-tenancy-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" className="text-xs">All tenancies</SelectItem>
            <SelectItem value="active" className="text-xs">Active tenancies</SelectItem>
            <SelectItem value="ended" className="text-xs">Ended tenancies</SelectItem>
            <SelectItem value="pending_consent" className="text-xs">Awaiting consent</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.payment_status || 'all'} onValueChange={setF('payment_status')}>
          <SelectTrigger className="h-8 w-[150px] text-xs bg-white border-slate-300" data-testid="filter-payment-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" className="text-xs">All payments</SelectItem>
            <SelectItem value="on_time" className="text-xs">On time</SelectItem>
            <SelectItem value="late" className="text-xs">Late</SelectItem>
            <SelectItem value="partial" className="text-xs">Partial</SelectItem>
            <SelectItem value="missed" className="text-xs">Missed</SelectItem>
          </SelectContent>
        </Select>
        {activeFilters > 0 && (
          <button type="button" onClick={() => setFilters({ property_id: '', city: '', tenancy_status: '', payment_status: '' })} className="ml-auto text-xs text-teal-700 hover:text-teal-800 font-medium" data-testid="clear-filters-btn">
            Clear {activeFilters} {activeFilters === 1 ? 'filter' : 'filters'}
          </button>
        )}
      </div>

      {!hasData ? (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center" data-testid="analytics-empty">
          <p className="text-sm text-slate-700">{activeFilters ? 'Nothing matches these filters.' : 'Analytics appear once you have a property with an active tenancy and at least one report.'}</p>
        </div>
      ) : (
        <>
          <AiSummary scope={scope} />

          <div className="bg-white border border-slate-200 rounded-lg grid grid-cols-2 lg:grid-cols-4 lg:divide-x divide-slate-100" data-testid="kpi-grid">
            <Kpi label="Monthly rent roll" value={money(k.rent_roll)} sub={`${k.active_tenancies} active ${k.active_tenancies === 1 ? 'tenancy' : 'tenancies'} · ${k.properties} ${k.properties === 1 ? 'property' : 'properties'}`} testId="kpi-rent-roll"
              spark={tr && { labels: tr.labels, values: tr.rent_roll, format: money }} onClick={drillTenancies} />
            <Kpi label="On-time collection" value={k.collection_rate == null ? '—' : `${k.collection_rate}%`} delta={hasPrev ? deltaPts(k.collection_rate, k.collection_rate_prev) : null} sub={judged ? `${mix.on_time} of ${judged} payments on time` : 'No payments reported in this period'} testId="kpi-collection"
              spark={tr && { labels: tr.labels, values: tr.collection_rate, format: (v) => `${v}%` }} onClick={() => drillReports(`Payments, ${RANGE_LABEL[timeRange].toLowerCase()}`, { time_range: timeRange })} />
            <Kpi label="Owing on filed reports" value={money(k.outstanding)} delta={hasPrev ? deltaMoney(k.outstanding, k.outstanding_prev) : null} sub={`${attention.length} ${attention.length === 1 ? 'entry' : 'entries'} late, partial or missed`} testId="kpi-outstanding"
              spark={tr && { labels: tr.labels, values: tr.outstanding, format: money, color: '#b91c1c' }} onClick={() => drillReports('Late, partial and missed', { time_range: timeRange, status: 'late,partial,missed' })} />
            <Kpi label="Reports filed" value={k.reports ?? 0} delta={hasPrev ? deltaCount(k.reports, k.reports_prev) : null} sub={`${k.open_disputes} open ${k.open_disputes === 1 ? 'dispute' : 'disputes'} · ${k.disputes_opened} opened in period`} testId="kpi-reports"
              spark={tr && { labels: tr.labels, values: tr.reports, format: (v) => v, color: '#64748b' }} onClick={() => drillReports(`Reports, ${RANGE_LABEL[timeRange].toLowerCase()}`, { time_range: timeRange })} />
          </div>

          {insights === null && <div className="grid lg:grid-cols-2 gap-4" data-testid="insights-loading">{[0, 1].map(i => <div key={i} className="h-56 bg-white rounded-lg animate-pulse" />)}</div>}
          {insights && <>
            <CollectionStatus data={insights.this_month} onDrill={setDrill} />
            <div className="grid lg:grid-cols-2 gap-4">
              <CollectionCurve curve={insights.curve} />
              <AgingCard aging={insights.aging} onDrill={setDrill} />
            </div>
          </>}

          <div className="bg-white border border-slate-200 rounded-lg" data-testid="payments-chart">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-800">Payments by month</h3>
              <span className="text-xs text-slate-400">Click a month to see who paid</span>
            </div>
            <div className="p-4">
              {judged > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={months} barCategoryGap="30%" style={{ cursor: 'pointer' }} onClick={(st) => { const m = months[Number(st?.activeIndex ?? st?.activeTooltipIndex)]; if (m) drillMonth(m); }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(15,23,42,0.06)' }} cursor={{ fill: '#f8fafc' }} />
                    <Legend iconType="square" iconSize={8} itemSorter={null} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    <Bar dataKey="on_time" stackId="p" name="On time" fill="#0f766e" />
                    <Bar dataKey="late" stackId="p" name="Late" fill="#f59e0b" />
                    <Bar dataKey="partial" stackId="p" name="Partial" fill="#fb923c" />
                    <Bar dataKey="missed" stackId="p" name="Missed" fill="#dc2626" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="h-[200px] flex items-center justify-center text-sm text-slate-500">No payments reported in this period.</p>
              )}
            </div>
          </div>

          {insights && <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3"><RiskWatchlist risk={insights.risk} onTenant={openTenant} /></div>
            <div className="lg:col-span-2"><PaymentHabits habits={insights.habits} scope={scope} onDrill={setDrill} /></div>
          </div>}

          <div className="bg-white border border-slate-200 rounded-lg" data-testid="by-property">
            <div className="px-5 py-3.5">
              <h3 className="text-sm font-medium text-slate-800">By property <span className="text-slate-400 font-normal">({byProperty.length})</span></h3>
            </div>
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-2.5 bg-slate-50/60 border-y border-slate-100 text-xs text-slate-500">
              {[['Property', 4, 'address'], ['Tenancies', 1, 'active_tenancies'], ['Monthly rent', 2, 'monthly_rent'], ['Reports', 1, 'reports'], ['On time', 1, 'collection_rate'], ['Late', 1, 'late'], ['Outstanding', 2, 'outstanding']].map(([l, span, key]) => (
                <button key={key} type="button" onClick={() => setPropSort(key)} className={`${{1:'col-span-1',2:'col-span-2',4:'col-span-4'}[span]} text-left hover:text-slate-800 transition-colors ${['monthly_rent', 'outstanding', 'reports', 'collection_rate', 'late', 'active_tenancies'].includes(key) ? 'text-right' : ''} ${propSort === key ? 'text-teal-700' : ''}`} data-testid={`prop-sort-${key}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
              {sortedProps.map((p) => (
                <a key={p.property_id} href={`/landlord/properties/${p.property_id}`} className="grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-slate-50/80 transition-colors" data-testid={`prop-row-${p.property_id}`}>
                  <div className="col-span-12 md:col-span-4 min-w-0">
                    <p className="text-sm text-teal-700 truncate">{split(p.address)[0]}</p>
                    <p className="text-xs text-slate-500 truncate">{split(p.address)[1]}{p.open_disputes > 0 && <span className="text-amber-700"> · {p.open_disputes} open {p.open_disputes === 1 ? 'dispute' : 'disputes'}</span>}</p>
                    <p className="md:hidden text-xs text-slate-600 tabular-nums mt-1">{p.active_tenancies} {p.active_tenancies === 1 ? 'tenant' : 'tenants'} · {money(p.monthly_rent)}/mo · <Rate v={p.collection_rate} /> collected{p.late ? <span className="text-amber-700"> · {p.late} late</span> : ''}{p.outstanding ? <span className="text-red-700"> · {money(p.outstanding)} owing</span> : ''}</p>
                  </div>
                  <div className="hidden md:block col-span-3 md:col-span-1 text-right text-sm text-slate-700 tabular-nums">{p.active_tenancies}</div>
                  <div className="hidden md:block col-span-3 md:col-span-2 text-right text-sm text-slate-700 tabular-nums">{money(p.monthly_rent)}</div>
                  <div className="hidden md:block col-span-2 md:col-span-1 text-right text-sm text-slate-700 tabular-nums">{p.reports}</div>
                  <div className="hidden md:block col-span-2 md:col-span-1 text-right text-sm tabular-nums"><Rate v={p.collection_rate} /></div>
                  <div className={`hidden md:block col-span-2 md:col-span-1 text-right text-sm tabular-nums ${p.late ? 'text-amber-700' : 'text-slate-400'}`}>{p.late}</div>
                  <div className={`hidden md:block col-span-2 text-right text-sm tabular-nums ${p.outstanding ? 'text-red-700' : 'text-slate-400'}`}>{p.outstanding ? money(p.outstanding) : '—'}</div>
                </a>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500" data-testid="by-property-footer">Showing {sortedProps.length} of {byProperty.length} {byProperty.length === 1 ? 'property' : 'properties'}</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg" data-testid="attention">
            <div className="px-5 py-3.5">
              <h3 className="text-sm font-medium text-slate-800">Late and missed payments <span className="text-slate-400 font-normal">({attention.length})</span></h3>
            </div>
            {attention.length === 0 ? (
              <p className="px-5 py-4 border-t border-slate-100 text-sm text-slate-500">All on time</p>
            ) : (
              <>
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-2.5 bg-slate-50/60 border-y border-slate-100 text-xs text-slate-500">
                  <div className="col-span-3">Tenant</div><div className="col-span-4">Property</div><div className="col-span-2">Period</div><div className="col-span-1">Status</div><div className="col-span-1 text-right">Days late</div><div className="col-span-1 text-right">Owing</div>
                </div>
                <div className="divide-y divide-slate-100">
                  {attention.map((a) => (
                    <button type="button" key={a.report_id} onClick={() => openTenant(a.tenant_id)} className="w-full text-left grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-slate-50/80 transition-colors" data-testid={`attention-row-${a.report_id}`}>
                      <div className="col-span-9 md:col-span-3 min-w-0">
                        <p className="text-sm text-teal-700 truncate">{a.tenant_name}</p>
                        <p className="text-xs text-slate-500">{a.tenant_unique_id || ''}{a.is_disputed ? ' · disputed' : ''}</p>
                        <p className="md:hidden text-xs text-slate-600 truncate mt-0.5">{a.address} · {fmtDate(a.period_end)}{a.amount_owing ? <span className="text-red-700"> · {money(a.amount_owing)}</span> : ''}</p>
                      </div>
                      <div className="hidden md:block col-span-4 text-sm text-slate-700 truncate">{a.address}</div>
                      <div className="hidden md:block col-span-2 text-sm text-slate-500">{fmtDate(a.period_end)}</div>
                      <div className="col-span-3 md:col-span-1 flex justify-end md:block"><Pill tone={a.status === 'missed' ? 'red' : 'amber'}><span className="capitalize">{a.status}</span></Pill></div>
                      <div className="hidden md:block col-span-1 text-right text-sm text-slate-700 tabular-nums">{a.days_late ?? '—'}</div>
                      <div className="hidden md:block col-span-1 text-right text-sm text-red-700 tabular-nums">{a.amount_owing ? money(a.amount_owing) : '—'}</div>
                    </button>
                  ))}
                </div>
                <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500" data-testid="attention-footer">Showing {attention.length} {attention.length === 1 ? 'payment' : 'payments'}</div>
              </>
            )}
          </div>
        </>
      )}
      <DrillSheet drill={drill} onClose={() => setDrill(null)} onTenant={openTenant} />
      <TenantPanel tenantId={tenantId} onClose={() => setTenantId(null)} />
    </div>
  );
}

/* ---- helpers ---- */
const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);
const fmtDate = (v) => (v ? new Date(v + (v.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const deltaPts = (cur, prev) => (cur == null || prev == null ? null : { text: `${cur - prev >= 0 ? '+' : ''}${cur - prev} pts`, good: cur - prev >= 0, zero: cur === prev });
const deltaMoney = (cur, prev) => (cur == null || prev == null ? null : { text: `${cur - prev >= 0 ? '+' : '−'}${money(Math.abs(cur - prev)).slice(1)}`, good: cur - prev <= 0, zero: cur === prev });
const deltaCount = (cur, prev) => (cur == null || prev == null ? null : { text: `${cur - prev >= 0 ? '+' : ''}${cur - prev}`, good: true, zero: cur === prev });

function Kpi({ label, value, sub, delta, testId, spark, onClick }) {
  return (
    <button type="button" onClick={onClick} className="text-left px-5 pt-3.5 pb-2 hover:bg-slate-50/70 transition-colors" title={sub} data-testid={testId}>
      <p className="text-xs text-slate-500">{label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <p className="text-xl font-medium text-slate-800 tabular-nums">{value}</p>
        {delta && !delta.zero && <span title="vs previous period" className={`text-xs ${delta.good ? 'text-emerald-700' : 'text-red-700'}`}>{delta.text}</span>}
      </div>
      <p className="text-[11px] text-slate-400 truncate mt-0.5">{sub}</p>
      {spark ? <Sparkline {...spark} testId={`${testId}-spark`} /> : <div className="h-8" />}
    </button>
  );
}

const split = (a = '') => { const i = a.lastIndexOf(','); return i < 0 ? [a, ''] : [a.slice(0, i), a.slice(i + 1).trim()]; };

function Rate({ v }) {
  if (v == null) return <span className="text-slate-400">—</span>;
  return <span className={v >= 90 ? 'text-emerald-700' : v >= 70 ? 'text-slate-700' : 'text-red-700'}>{v}%</span>;
}
